import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ActivityLog } from "../api/api";
import {
  activityLabel,
  normalizeDiff,
  isFieldChanged,
  snapshotEntries,
  formatFieldValue,
  fieldLabel,
  fmtDate,
  BranchRef,
  PartnerRef,
} from "../lib/activityLog";

interface ActivityLogModalProps {
  log: ActivityLog | null;
  onClose: () => void;
  branches?: BranchRef[];
  partners?: PartnerRef[];
  /** Shows "<model_name> #<record_id>" alongside the action label — for contexts where the record isn't already implied by the page (e.g. the global audit trail). */
  showContext?: boolean;
}

const ACCENT_BY_ACTION: Record<string, string> = {
  create: "border-l-status-ok-fg",
  delete: "border-l-status-alert-fg",
};

export default function ActivityLogModal({ log, onClose, branches, partners, showContext }: ActivityLogModalProps) {
  const isSnapshot = log?.action === "create" || log?.action === "delete";
  const diff = log && !isSnapshot ? normalizeDiff(log.action, log.payload) ?? {} : null;
  const diffEntries = diff ? Object.entries(diff) : [];
  const changedCount = diffEntries.filter(([key, entry]) => isFieldChanged(key, entry)).length;
  const snapshot = log && isSnapshot ? snapshotEntries(log.payload) : [];

  return (
    <Dialog open={!!log} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={log ? `max-w-2xl border-l-4 ${ACCENT_BY_ACTION[log.action] ?? "border-l-waves-zee"}` : undefined}>
        {log && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-10 text-xl">
                {activityLabel(log.action, log.payload, log.model_name)}
              </DialogTitle>
              <p className="text-sm text-waves-muted">
                <span className="font-mono">{fmtDate(log.created_at)}</span> · {log.actor_email}
                {showContext && ` — ${log.model_name.replace(/_/g, " ")} #${log.record_id}`}
                {diff && changedCount > 0 && ` · ${changedCount} ${changedCount === 1 ? "veld" : "velden"} gewijzigd`}
              </p>
            </DialogHeader>

            {isSnapshot ? (
              <div className="grid divide-y divide-status-neutral-bg text-sm">
                {snapshot.map(([key, value]) => (
                  <div key={key} className="grid gap-x-4 py-2.5 first:pt-0" style={{ gridTemplateColumns: "140px 1fr" }}>
                    <span className="text-waves-muted">{fieldLabel(key, log.model_name)}</span>
                    <span className="break-words font-medium">{formatFieldValue(key, value, log.model_name, branches, partners)}</span>
                  </div>
                ))}
                {snapshot.length === 0 && (
                  <span className="py-2.5 text-waves-muted">Geen details beschikbaar.</span>
                )}
              </div>
            ) : (
              <div className="text-sm">
                {diffEntries.length > 0 && (
                  <div className="grid gap-x-4 pb-2 text-[11px] font-bold uppercase tracking-label text-waves-muted" style={{ gridTemplateColumns: "140px 1fr 1fr" }}>
                    <span>Veld</span>
                    <span>Voor</span>
                    <span>Na</span>
                  </div>
                )}
                <div className="grid divide-y divide-status-neutral-bg">
                  {diffEntries.map(([key, entry]) => {
                    const changed = isFieldChanged(key, entry);
                    return (
                      <div key={key} className="grid gap-x-4 py-2.5 first:pt-0" style={{ gridTemplateColumns: "140px 1fr 1fr" }}>
                        <span className="text-waves-muted">{fieldLabel(key, log.model_name)}</span>
                        <span className={changed ? "break-words font-medium text-status-alert-fg line-through decoration-status-alert-fg/40" : "break-words font-medium"}>
                          {formatFieldValue(key, entry.old, log.model_name, branches, partners)}
                        </span>
                        <span className={changed ? "break-words font-bold text-status-ok-fg" : "break-words font-medium"}>
                          {formatFieldValue(key, entry.new, log.model_name, branches, partners)}
                        </span>
                      </div>
                    );
                  })}
                  {diffEntries.length === 0 && (
                    <span className="py-2.5 text-waves-muted">Geen details beschikbaar.</span>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
